import { z } from 'zod';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import {
  ACCESS_LEVEL_VALUES,
  CONFIRMATION_STATUS_VALUES,
  EMPLOYMENT_TYPE_VALUES,
  isBlankPhoneNumber,
  isFieldStaffRole,
  isFulfilmentRole,
  ROLE_VALUES,
} from '../constants';
import type { CreateUserData } from '@api/users';

const EMAIL_REGEX = /^[^,;@ \r\n]+@[^,@; \r\n]+\.[^,@; \r\n]+$/;

const NRIC_REGEX = /^[STFGM]\d{7}[A-Z]$/;

// Role type derived from constants
export type UserRole = typeof ROLE_VALUES[number];

// Validation message keys (match i18n keys in users.form.validation)
export type ValidationMessages = {
  nameRequired?: string;
  nameMaxLength?: string;
  emailRequired?: string;
  emailInvalid?: string;
  phoneInvalid?: string;
  roleRequired?: string;
  confirmationStatusRequired?: string;
  accessLevelRequired?: string;
  nricRequired?: string;
  nricInvalid?: string;
  slingUserIdRequired?: string;
  slingPositionRequired?: string;
};

// Default validation messages (fallbacks - actual messages come from i18n)
const DEFAULT_VALIDATION_MESSAGES: Required<ValidationMessages> = {
  nameRequired: 'Name is required',
  nameMaxLength: 'Name must be less than 100 characters',
  emailRequired: 'Email is required',
  emailInvalid: 'Invalid email format',
  phoneInvalid: 'Invalid phone number',
  roleRequired: 'Please select an access level',
  confirmationStatusRequired: 'Please select a confirmation status',
  accessLevelRequired: 'Please select venue access',
  nricRequired: 'NRIC / FIN is required',
  nricInvalid: 'Enter an NRIC / FIN like S1234567A',
  slingUserIdRequired: 'Enter their Sling ID too',
  slingPositionRequired: 'Pick their position too',
};

/**
 * Both or neither: both empty means not linked to Sling. Only fulfilment roles
 * carry a link, so the rule is skipped for everyone else.
 */
function refineSlingLink(
  values: { role: UserRole; sling_user_id: string; sling_position_id: string },
  ctx: z.RefinementCtx,
  m: Required<ValidationMessages>,
) {
  if (!isFulfilmentRole(values.role)) return;

  const slingUserId = values.sling_user_id.trim();
  if (slingUserId !== '' && values.sling_position_id === '') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sling_position_id'], message: m.slingPositionRequired });
  }
  if (slingUserId === '' && values.sling_position_id !== '') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sling_user_id'], message: m.slingUserIdRequired });
  }
}

/**
 * Creates a createUserForm schema with customizable validation messages
 */
export function createUserFormSchemaWithMessages(messages?: ValidationMessages) {
  const m = { ...DEFAULT_VALIDATION_MESSAGES, ...messages };

  return z.object({
    name: z.string()
      .trim()
      .min(1, m.nameRequired)
      .max(100, m.nameMaxLength),

    email: z.string()
      .trim()
      .min(1, m.emailRequired)
      .regex(EMAIL_REGEX, m.emailInvalid),

    mobile_number: z.string()
      .refine(
        (val) => {
          if (isBlankPhoneNumber(val)) return true;
          try {
            const phoneNumber = parsePhoneNumberWithError(val);
            return phoneNumber.isValid();
          } catch {
            return false;
          }
        },
        { message: m.phoneInvalid }
      ),

    role: z.enum(ROLE_VALUES, {
      message: m.roleRequired
    }),
    employment_type: z.enum(EMPLOYMENT_TYPE_VALUES),
    confirmation_status: z.enum(['', ...CONFIRMATION_STATUS_VALUES]),
    access_level: z.enum(['', ...ACCESS_LEVEL_VALUES]),
    nric: z.string(),
    vehicle_number: z.string(),
    sling_user_id: z.string().regex(/^\d*$/),
    sling_position_id: z.string(),
  }).superRefine((values, ctx) => {
    refineSlingLink(values, ctx, m);

    if (!isFieldStaffRole(values.role)) return;

    if (!values.confirmation_status) {
      ctx.addIssue({ code: 'custom', path: ['confirmation_status'], message: m.confirmationStatusRequired });
    }
    if (!values.access_level) {
      ctx.addIssue({ code: 'custom', path: ['access_level'], message: m.accessLevelRequired });
    }
    const nric = values.nric.trim().toUpperCase();
    if (!nric) {
      ctx.addIssue({ code: 'custom', path: ['nric'], message: m.nricRequired });
    } else if (!NRIC_REGEX.test(nric)) {
      ctx.addIssue({ code: 'custom', path: ['nric'], message: m.nricInvalid });
    }
  });
}

/**
 * Creates an updateUserForm schema with customizable validation messages
 */
export function updateUserFormSchemaWithMessages(messages?: ValidationMessages) {
  const m = { ...DEFAULT_VALIDATION_MESSAGES, ...messages };

  return z.object({
    name: z.string()
      .trim()
      .min(1, m.nameRequired)
      .max(100, m.nameMaxLength),

    mobile_number: z.string()
      .refine(
        (val) => {
          if (isBlankPhoneNumber(val)) return true;
          try {
            const phoneNumber = parsePhoneNumberWithError(val);
            return phoneNumber.isValid();
          } catch {
            return false;
          }
        },
        { message: m.phoneInvalid }
      ),

    role: z.enum(ROLE_VALUES, {
      message: m.roleRequired
    }),

    shown_in_catering_referral: z.boolean(),
    confirmation_status: z.enum(['', ...CONFIRMATION_STATUS_VALUES]),
    access_level: z.enum(['', ...ACCESS_LEVEL_VALUES]),
    employment_type: z.enum(EMPLOYMENT_TYPE_VALUES),
    nric: z.string(),
    vehicle_number: z.string(),
    saved_nric: z.string().nullable(),
    sling_user_id: z.string().regex(/^\d*$/),
    sling_position_id: z.string(),
  }).superRefine((values, ctx) => {
    refineSlingLink(values, ctx, m);

    if (!isFieldStaffRole(values.role)) return;

    const nric = values.nric.trim().toUpperCase();
    if (nric !== '' && !NRIC_REGEX.test(nric)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['nric'], message: m.nricInvalid });
    }

    if (values.confirmation_status === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['confirmation_status'], message: m.confirmationStatusRequired });
    }
    if (values.access_level === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['access_level'], message: m.accessLevelRequired });
    }
    if (nric === '' && values.saved_nric === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['nric'], message: m.nricRequired });
    }
  });
}

// Static schemas with default messages (for backwards compatibility)
export const createUserFormSchema = createUserFormSchemaWithMessages();
export const updateUserFormSchema = updateUserFormSchemaWithMessages();

export type CreateUserPayload = CreateUserData;

export interface UpdateUserPayload {
  id: number;
  name: string;
  mobile_number?: string;
  status?: 'verified' | 'closed';
  role: UserRole;
  shown_in_catering_referral?: boolean;
}

export type CreateUserFormData = z.infer<typeof createUserFormSchema>;
export type UpdateUserFormData = z.infer<typeof updateUserFormSchema>;
